import * as THREE from 'three'
import gsap from 'gsap'

import AreaFloorBorderGeometry from '../Geometries/AreaFloorBorderGeometry.js'
import AreaFenceGeometry from '../Geometries/AreaFenceGeometry.js'
import AreaFenceMaterial from '../Materials/AreaFence.js'
import AreaFloorBorderMaterial from '../Materials/AreaFloorBorder.js'
import { BUILDING_TYPES, TUNING } from '../Game/definitions.js'
import OffscreenArrow from './OffscreenArrow.js'

export const createTextSprite = (_text, _options = {}) =>
{
    const canvas = document.createElement('canvas')
    canvas.width = 768
    canvas.height = 160
    const context = canvas.getContext('2d')
    context.fillStyle = _options.background ?? 'rgba(20, 15, 31, 0.86)'
    context.beginPath()
    context.roundRect(10, 10, 748, 140, 36)
    context.fill()
    context.fillStyle = _options.color ?? '#d8ffe5'
    context.font = '700 42px "Oswald", "Arial Narrow", sans-serif'
    context.textAlign = 'center'
    context.textBaseline = 'middle'
    context.fillText(_text, 384, 82)
    const texture = new THREE.CanvasTexture(canvas)
    texture.colorSpace = THREE.SRGBColorSpace
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: texture, transparent: true, depthWrite: false }))
    sprite.scale.set(5.2, 1.08, 1)
    return sprite
}

export const createQuantitySprite = () =>
{
    const canvas = document.createElement('canvas')
    canvas.width = 128
    canvas.height = 128
    const texture = new THREE.CanvasTexture(canvas)
    texture.colorSpace = THREE.SRGBColorSpace
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: texture, transparent: true, depthWrite: false, depthTest: false }))
    sprite.scale.set(0.7, 0.7, 1)
    sprite.renderOrder = 5
    sprite.userData = { canvas, texture }
    return sprite
}

export const updateQuantitySprite = (_sprite, _value) =>
{
    const context = _sprite.userData.canvas.getContext('2d')
    context.clearRect(0, 0, 128, 128)
    context.shadowColor = 'rgba(111, 48, 20, 0.4)'
    context.shadowBlur = 6
    context.shadowOffsetY = 3
    context.fillStyle = '#fff7e9'
    context.font = '700 92px "Oswald", "Arial Narrow", sans-serif'
    context.textAlign = 'center'
    context.textBaseline = 'middle'
    context.fillText(`${_value}`, 64, 66)
    _sprite.userData.texture.needsUpdate = true
}

export default class InteractionTile
{
    constructor(_options)
    {
        this.time = _options.time
        this.site = _options.site
        this.factory = _options.factory
        this.container = new THREE.Group()
        this.container.position.set(this.site.x, this.site.y, 0.04)
        this.offscreenArrow = new OffscreenArrow({ camera: _options.camera, position: new THREE.Vector3(this.site.x, this.site.y, 1.5) })
        this.size = TUNING.tileHalfSize * 2
        this.lastKey = ''

        this.borderMaterial = new AreaFloorBorderMaterial()
        this.borderMaterial.uniforms.uColor.value = new THREE.Color(0xffffff)
        this.borderMaterial.uniforms.uAlpha.value = 0.65
        this.borderMaterial.uniforms.uLoadProgress.value = 1
        this.borderMaterial.uniforms.uProgress.value = 1
        const border = new THREE.Mesh(new AreaFloorBorderGeometry(this.size, this.size, 0.18), this.borderMaterial)
        border.renderOrder = 1
        this.container.add(border)

        this.fenceMaterial = new AreaFenceMaterial()
        this.fenceMaterial.uniforms.uBorderAlpha.value = 0.65
        this.fenceMaterial.uniforms.uStrikeAlpha.value = 0.35
        this.fenceDepth = 0.65
        this.fenceOffset = 0.45
        this.fence = new THREE.Mesh(new AreaFenceGeometry(this.size, this.size, this.fenceDepth), this.fenceMaterial)
        this.fence.position.z = - this.fenceDepth
        this.fence.renderOrder = 2
        this.container.add(this.fence)
        this.fenceActive = false

        this.labelCanvas = document.createElement('canvas')
        this.labelCanvas.width = 1024
        this.labelCanvas.height = 256
        this.labelTexture = new THREE.CanvasTexture(this.labelCanvas)
        this.labelTexture.colorSpace = THREE.SRGBColorSpace
        this.label = new THREE.Mesh(
            new THREE.PlaneGeometry(this.size + 1, (this.size + 1) * 0.25),
            new THREE.MeshBasicMaterial({ map: this.labelTexture, transparent: true, depthWrite: false, depthTest: true, side: THREE.DoubleSide })
        )
        this.label.position.set(0.5, -2.4, 0.09)
        this.label.renderOrder = 4
        this.container.add(this.label)

        this.recipeContainer = new THREE.Group()
        this.recipeContainer.position.z = 2.15
        this.container.add(this.recipeContainer)
        this.recipeItems = []
        const resources = Object.keys(this.site.recipe)
        resources.forEach((_resource, _index) =>
        {
            const group = new THREE.Group()
            group.position.x = (_index - (resources.length - 1) * 0.5) * 1.25
            const item = this.factory.createItem(_resource, 1.35)
            item.position.set(-0.28, 0, 0)
            const quantity = createQuantitySprite()
            quantity.position.set(0.42, 0, 0.12)
            group.add(item, quantity)
            this.recipeContainer.add(group)
            this.recipeItems.push({ group, resource: _resource, quantity, offset: _index * 1.8, completed: false })
        })

        this.pin = this.factory.createPin()
        this.pin.position.z = 3.4
        this.container.add(this.pin)
        this.pinVisibleUntil = this.time.elapsed + 5000
        this.pinFadeDuration = 600
        this.pinOpacity = 0
        this.pinMaterials = []
        const pinMaterials = new Set()
        this.pin.traverse((_child) =>
        {
            if(!_child.isMesh)
                return
            _child.material = _child.material.clone()
            pinMaterials.add(_child.material)
        })
        for(const material of pinMaterials)
        {
            this.pinMaterials.push({
                material,
                opacity: material.uniforms?.opacity?.value ?? material.opacity,
                alpha: material.uniforms?.uAlpha?.value,
                transparent: material.transparent
            })
        }
        this.setPinOpacity(0)
        this.sync()
        document.fonts.ready.then(() =>
        {
            this.lastKey = ''
            this.sync()
        })
    }

    showPin()
    {
        this.pinVisibleUntil = this.time.elapsed + 5000
    }

    destroy()
    {
        this.offscreenArrow.remove()
    }

    setPinOpacity(_opacity)
    {
        for(const { material, opacity, alpha, transparent } of this.pinMaterials)
        {
            material.transparent = transparent || _opacity < 1
            if(material.uniforms?.uAlpha)
                material.uniforms.uAlpha.value = alpha * _opacity
            else if(material.uniforms?.opacity)
                material.uniforms.opacity.value = opacity * _opacity
            else
                material.opacity = opacity * _opacity
        }
    }

    sync()
    {
        const key = Object.keys(this.site.recipe).map((_resource) => this.site.deposited[_resource]).join('-')
        if(key === this.lastKey)
            return
        this.lastKey = key
        const context = this.labelCanvas.getContext('2d')
        context.clearRect(0, 0, 1024, 256)
        context.shadowColor = 'rgba(52, 255, 145, 0.2)'
        context.shadowBlur = 4
        context.shadowOffsetY = 2
        context.fillStyle = '#d8ffe5'
        const title = BUILDING_TYPES[this.site.buildingType].label.toUpperCase()
        let fontSize = 190
        context.font = `700 ${fontSize}px "Oswald", "Arial Narrow", sans-serif`
        while(context.measureText(title).width > 960 && fontSize > 80)
        {
            fontSize -= 4
            context.font = `700 ${fontSize}px "Oswald", "Arial Narrow", sans-serif`
        }
        context.textAlign = 'left'
        context.fillText(title, 24, 194)
        this.labelTexture.needsUpdate = true
        for(const item of this.recipeItems)
        {
            const remaining = this.site.recipe[item.resource] - this.site.deposited[item.resource]
            updateQuantitySprite(item.quantity, remaining)
            if(remaining === 0 && !item.completed)
            {
                item.completed = true
                gsap.killTweensOf(item.group.scale)
                gsap.to(item.group.scale, {
                    x: 0,
                    y: 0,
                    z: 0,
                    duration: 0.35,
                    ease: 'back.in(2)',
                    onComplete: () => item.group.visible = false
                })
            }
        }
        this.layoutRecipe()
    }

    layoutRecipe()
    {
        const outstandingItems = this.recipeItems.filter((_item) => !_item.completed)
        outstandingItems.forEach((_item, _index) =>
        {
            gsap.killTweensOf(_item.group.position)
            gsap.to(_item.group.position, {
                x: (_index - (outstandingItems.length - 1) * 0.5) * 1.25,
                duration: 0.45,
                ease: 'power2.inOut'
            })
        })
    }

    update(_active, _guided = false)
    {
        this.fenceMaterial.uniforms.uTime.value = this.time.elapsed
        if(_active !== this.fenceActive)
        {
            this.fenceActive = _active
            gsap.killTweensOf(this.fence.position)
            gsap.killTweensOf(this.borderMaterial.uniforms.uAlpha)
            gsap.killTweensOf(this.fenceMaterial.uniforms.uBorderAlpha)
            gsap.to(this.fence.position, {
                z: _active ? this.fenceOffset : - this.fenceDepth,
                duration: _active ? 0.35 : 0.25,
                ease: _active ? 'back.out(3)' : 'back.in(3)'
            })
            gsap.to(this.borderMaterial.uniforms.uAlpha, { value: _active ? 0.95 : 0.65, duration: 0.35 })
            gsap.to(this.fenceMaterial.uniforms.uBorderAlpha, { value: _active ? 0.95 : 0.65, duration: 0.35 })
        }
        for(const item of this.recipeItems)
        {
            item.group.position.z = Math.sin(this.time.elapsed * 0.004 + item.offset) * 0.1
            item.group.rotation.z = Math.sin(this.time.elapsed * 0.0025 + item.offset) * 0.06
        }
        this.pin.position.z = 3.4 + Math.sin(this.time.elapsed * 0.007) * 0.35
        const haloScale = 1 + Math.sin(this.time.elapsed * 0.009) * 0.18
        this.pin.userData.halo.scale.setScalar(haloScale)
        const targetOpacity = _guided || this.time.elapsed < this.pinVisibleUntil ? 1 : 0
        const maxChange = Math.min(this.time.delta, 60) / this.pinFadeDuration
        this.pinOpacity = THREE.MathUtils.clamp(targetOpacity, this.pinOpacity - maxChange, this.pinOpacity + maxChange)
        this.setPinOpacity(this.pinOpacity * this.pinOpacity * (3 - 2 * this.pinOpacity))
        this.pin.visible = this.pinOpacity > 0
        this.offscreenArrow.update(this.pin.visible)
    }
}
